/* * Copyright (c) Microsoft Corporation. All rights reserved. * Licensed under the MIT License. */
import { LogLevel } from "@azure/msal-browser"
/** * Enter here the user flows and custom policies for your B2C application * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview */
export const b2cPolicies = {
  names: { signIn: "B2C_1A_SIGNIN" },
  authorities: {
    signUp: { authority: process.env.REACT_APP_AD_B2C_SIGNUP_URI },
    signIn: { authority: process.env.REACT_APP_AD_B2C_SIGNIN_URI },
    passwordChange: {
      authority: process.env.REACT_APP_AD_B2C_PASSWORDCHANGE_URI,
    },
    resetPassword: {
      authority: process.env.REACT_APP_AD_B2C_PASSWORDRESET_URI,
    },
  },
  authorityDomain: process.env.REACT_APP_AD_B2C_TENANT_URI,
}
/** * Configuration object to be passed to MSAL instance on creation. * For a full list of MSAL.js configuration parameters, visit: * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md */
export const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AD_B2C_APP_CLIENT_ID as string,
    // This is the ONLY mandatory field that you need to supply.
    authority: process.env.REACT_APP_AD_B2C_SIGNIN_URI as string,
    knownAuthorities: [b2cPolicies.authorityDomain as string],
    // Mark your B2C tenant's domain as trusted.
    redirectUri: "/",
    // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: "/", // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: "sessionStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false,
    // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean,
      ) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message)
            return
          case LogLevel.Info:
            console.info(message)
            return
          case LogLevel.Verbose:
            console.debug(message)
            return
          case LogLevel.Warning:
            console.warn(message)
            return
          default:
            return
        }
      },
    },
  },
}
export const scopes = {
  read: [process.env.REACT_APP_AD_B2C_API_ENDPOINT_READ as string],
  write: [process.env.REACT_APP_AD_B2C_API_ENDPOINT_WRITE as string],
}
/** * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see: * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md */
export const protectedResources = {
  apiGreenSkills: {
    endpoint: process.env.REACT_APP_API_URI as string,
    scopes: {
      read: [process.env.REACT_APP_AD_B2C_API_ENDPOINT_READ as string],
      write: [process.env.REACT_APP_AD_B2C_API_ENDPOINT_WRITE as string],
    },
  },
}
/** * Scopes you add here will be prompted for user consent during sign-in. * By default, MSAL.js will add OIDC scopes (openid, profile, email * For more information about OIDC scopes, visit: * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes */
export const loginRequest = {
  scopes: [
    ...protectedResources.apiGreenSkills.scopes.read,
    ...protectedResources.apiGreenSkills.scopes.write,
  ],
  authority: b2cPolicies.authorities.signIn.authority,
}
export const resetPasswordRequest = {
  scopes: [
    ...protectedResources.apiGreenSkills.scopes.read,
    ...protectedResources.apiGreenSkills.scopes.write,
  ],
  authority: b2cPolicies.authorities.resetPassword.authority,
}
export const registerRequest = {
  scopes: [
    ...protectedResources.apiGreenSkills.scopes.read,
    ...protectedResources.apiGreenSkills.scopes.write,
  ],
  authority: b2cPolicies.authorities.signUp.authority,
} // export const silentRequest = { // scopes: ["openid", "profile"], // loginHint: "example@domain.net", // };
export const passwordChangeRequest = {
  scopes: [
    ...protectedResources.apiGreenSkills.scopes.read,
    ...protectedResources.apiGreenSkills.scopes.write,
  ],
  authority: b2cPolicies.authorities.passwordChange.authority,
}
